import { useEffect, useState, useCallback, useMemo } from "react";
import WindCharts from "../components/WindCharts";
import api from "../utils/api";
import WindCard from "../components/WindCard";
import BuildingPlan from "../components/BuildingPlan";
import StatusBar from "../components/StatusBar";
import FetchLogViewer from "../components/FetchLogViewer";
import { useAuth } from "../hooks/useAuth";
import AnalysisTab from "../components/AnalysisTab";
import { sensorLage } from "../utils/wind";

const sortedByName = arr => [...arr].sort((a,b) => (a.name ?? a.sensor_name ?? "").localeCompare(b.name ?? b.sensor_name ?? "", undefined, {numeric:true}));

const TABS = ["Dashboard", "Gebäudeplan", "Verlauf", "Analyse", "Monitoring"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const isAdmin = user?.is_admin;
  const [tab, setTab] = useState("Dashboard");
  const [measurements, setMeasurements] = useState([]);
  const [sensors, setSensors] = useState([]);
  const analysisSensors = useMemo(
    () => sortedByName(sensors).map(s => ({ ...s, name: s.name })),
    [sensors]
  );
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyHours, setHistoryHours] = useState(24);
  const [autoSelected, setAutoSelected] = useState(false);
  const [visibleSensors, setVisibleSensors] = useState({});

  useEffect(() => {
    if (!autoSelected && measurements.length > 0) {
      const first = measurements.find(s => s.sensor_name === "WM-1") || measurements[0];
      setSelected(first);
      loadHistory(first.sensor_id, historyHours);
      setAutoSelected(true);
    }
  }, [measurements]);

  const [logs, setLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        api.get("/measurements/latest"),
        api.get("/sensors/"),
      ]);
      setMeasurements(mRes.data);
      setSensors(prev => {
        const newJson = JSON.stringify(sRes.data);
        const oldJson = JSON.stringify(prev);
        return newJson === oldJson ? prev : sRes.data;
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.post("/sensors/fetch-now").catch(() => {}).finally(() => loadData());
    const t = setInterval(loadData, 26000);
    return () => clearInterval(t);
  }, [loadData]);

  useEffect(() => {
    if (tab === "Monitoring") {
      api.get("/measurements/fetch-logs?limit=50").then(r => setLogs(r.data)).catch(() => {});
      if (isAdmin) {
        api.get("/auth/login-logs?limit=100").then(r => setLoginLogs(r.data)).catch(() => {});
      }
    }
  }, [tab, isAdmin]);

  const loadHistory = useCallback(async (sensorId, hours) => {
    const r = await api.get(`/measurements/history/${sensorId}?hours=${hours}`);
    setHistory(r.data);
  }, []);

  const handleCardClick = (sensor) => {
    setSelected(sensor);
    setTab("Verlauf");
    loadHistory(sensor.sensor_id, historyHours);
  };

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <div style={styles.navLeft}>
          <span style={styles.navLogo}>🌬️</span>
          <span style={styles.navTitle}>WindMess</span>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={styles.navSub}>Unterer Rheinweg 48-52, 4057 Basel</span>
            <span style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.04em" }}>by Kull Solutions GmbH</span>
          </div>
        </div>
        <div style={styles.tabs}>
          {TABS.map(t => (
            <button key={t} style={{ ...styles.tab, ...(tab === t ? styles.tabActive : {}) }} onClick={() => setTab(t)}>
              {t}
            </button>
          ))}
        </div>
        <div style={styles.navRight}>
          {isAdmin && <span style={styles.adminBadge}>Admin</span>}
          <span style={styles.userLabel}>{user?.username}</span>
          <button style={styles.logoutBtn} onClick={logout}>Abmelden</button>
        </div>
      </nav>

      <StatusBar onRefresh={loadData} isAdmin={isAdmin} />

      <main style={styles.main}>
        {loading ? (
          <div style={styles.loading}>Daten werden geladen…</div>
        ) : (
          <>
            {tab === "Dashboard" && <DashboardTab measurements={measurements} onCardClick={handleCardClick} />}
            {tab === "Gebäudeplan" && <BuildingPlan sensors={sortedByName(sensors).map(s => ({ ...s, ...(measurements.find(m => m.sensor_id === s.id) || {}), name: s.name }))} onUpdate={loadData} />}
            {tab === "Dashboard" && <WindCharts sensors={sortedByName(sensors).map(s => ({ ...s, ...(measurements.find(m => m.sensor_id === s.id) || {}), name: s.name }))} />}
            {tab === "Verlauf" && (
              <HistoryTab
                sensors={sortedByName(measurements)}
                selected={selected}
                history={history}
                hours={historyHours}
                onSelect={(s) => { setSelected(s); loadHistory(s.sensor_id, historyHours); }}
                onHoursChange={(h) => { setHistoryHours(h); if (selected) loadHistory(selected.sensor_id, h); }}
              />
            )}
            {tab === "Analyse" && (
              <AnalysisTab
                sensors={analysisSensors}
                visibleSensors={visibleSensors}
                setVisibleSensors={setVisibleSensors}
              />
            )}
            {tab === "Monitoring" && (
              <MonitoringTab
                logs={logs}
                loginLogs={loginLogs}
                sensors={sortedByName(sensors)}
                isAdmin={isAdmin}
                onRefresh={() => {
                  loadData();
                  api.get("/measurements/fetch-logs?limit=50").then(r => setLogs(r.data));
                  if (isAdmin) api.get("/auth/login-logs?limit=100").then(r => setLoginLogs(r.data));
                }}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}


function DashboardTab({ measurements, onCardClick }) {
  const sorted = [...measurements].sort((a, b) => (a.sensor_name ?? "").localeCompare(b.sensor_name ?? "", undefined, {numeric: true}));
  return (
    <div>
      <div style={styles.gridHeader}>
        <h2 style={styles.sectionTitle}>Aktuelle Windmessungen</h2>
        <span style={styles.gridHint}>Auf eine Karte klicken für den Verlauf</span>
      </div>
      <div style={styles.grid}>
        {sorted.map(s => <WindCard key={s.sensor_id} sensor={s} onClick={() => onCardClick(s)} />)}
      </div>
    </div>
  );
}

function HistoryTab({ sensors, selected, history, hours, onSelect, onHoursChange }) {
  return (
    <div style={{ display: "flex", gap: 20 }}>
      <div style={styles.sensorList}>
        <div style={styles.sensorListTitle}>Sensor wählen</div>
        {sensors.map(s => (
          <div
            key={s.sensor_id}
            style={{ ...styles.sensorItem, background: selected?.sensor_id === s.sensor_id ? "#1d4ed8" : "#1e293b" }}
            onClick={() => onSelect(s)}
          >
            <span style={{ fontWeight: 600 }}>{s.sensor_name}</span>
            {sensorLage(s) && <span style={{ color: "#e2e8f0", fontSize: 11, display: "block", marginTop: 2 }}>{sensorLage(s)}</span>}
          </div>
        ))}
      </div>
      <div style={{ flex: 1 }}>
        {selected
          ? <HistoryChart sensor={selected} data={history} hours={hours} onHoursChange={onHoursChange} />
          : <div style={styles.noSelection}>← Sensor auswählen</div>}
      </div>
    </div>
  );
}

function HistoryChart({ sensor, data, hours, onHoursChange }) {
  const [Recharts, setRecharts] = useState(null);
  useEffect(() => { import("recharts").then(setRecharts); }, []);
  if (!Recharts) return <div style={styles.loading}>Chart wird geladen…</div>;

  const { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } = Recharts;
  const chartData = data.map(d => ({
    time: hours <= 24
      ? new Date(d.measured_at).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" })
      : new Date(d.measured_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
    "Windgeschw.": d.wind_speed != null ? +d.wind_speed.toFixed(1) : null,
    "Boe": d.wind_gust != null ? +d.wind_gust.toFixed(1) : null,
  }));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={styles.sectionTitle}>{sensor.sensor_name} – Verlauf</h2>
        <div style={styles.hourBtns}>
          {[
            { h: 6,   label: "6 Std." },
            { h: 12,  label: "12 Std." },
            { h: 24,  label: "1 Tag" },
            { h: 48,  label: "2 Tage" },
            { h: 168, label: "7 Tage" },
          ].map(({ h, label }) => (
            <button key={h} style={{ ...styles.hourBtn, background: hours === h ? "#1d4ed8" : "#334155" }} onClick={() => onHoursChange(h)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {chartData.length === 0
        ? <div style={styles.noData}>Keine Daten im gewählten Zeitraum</div>
        : (
          <div style={{ background: "#1e293b", borderRadius: 12, padding: 16 }}>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="time" stroke="#64748b" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} unit=" m/s" />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }} labelStyle={{ color: "#94a3b8" }} />
                <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
                <Line type="monotone" dataKey="Windgeschw." stroke="#3b82f6" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="Boe" stroke="#f97316" dot={false} strokeWidth={2} strokeDasharray="4 2" connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ color: "#475569", fontSize: 11, textAlign: "right", marginTop: 4 }}>
              {chartData.length} Messpunkte · m/s
            </div>
          </div>
        )}
      {data.length > 0 && (
        <HistoryTable sensor={sensor} data={data} hours={hours} />
      )}
    </div>
  );
}

function degToCompass(deg) {
  if (deg == null) return "-";
  const dirs = ["N","NNO","NO","ONO","O","OSO","SO","SSO","S","SSW","SW","WSW","W","WNW","NW","NNW"];
  return dirs[Math.round(deg / 22.5) % 16] + " (" + Math.round(deg) + "°)";
}

// Zeitstempel ohne Komma: "19.03.2026 15:50:00"
function fmtTs(isoString) {
  const d = new Date(isoString);
  const dd   = String(d.getDate()).padStart(2, "0");
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh   = String(d.getHours()).padStart(2, "0");
  const min  = String(d.getMinutes()).padStart(2, "0");
  const ss   = String(d.getSeconds()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${min}:${ss}`;
}

function HistoryTable({ sensor, data, hours }) {
  const lage = sensorLage(sensor);

  // ── CSV Export Wind ──────────────────────────────────────────────
  const exportCsvWind = () => {
    const BOM = "\ufeff";
    const header = "Zeitstempel;Lage;Windgeschw. (m/s);Boe (m/s);Richtung (Grad);Richtung (Kompass);Temp (C);Feuchte (%)";
    const rows = [...data].reverse().map(d => [
      fmtTs(d.measured_at),
      lage,
      d.wind_speed     != null ? d.wind_speed.toFixed(2)        : "",
      d.wind_gust      != null ? d.wind_gust.toFixed(2)         : "",
      d.wind_direction != null ? Math.round(d.wind_direction)   : "",
      d.wind_direction != null ? degToCompass(d.wind_direction) : "",
      d.temperature    != null ? d.temperature.toFixed(1)       : "",
      d.humidity       != null ? Math.round(d.humidity)         : "",
    ].join(";"));
    const blob = new Blob([BOM + header + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sensor.sensor_name}_wind_${hours}h.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── CSV Export Alles ─────────────────────────────────────────────
  const exportCsvFull = () => {
    const BOM = "\ufeff";
    const header = "Zeitstempel;Lage;Windgeschw. (m/s);Boe (m/s);Richtung (Grad);Richtung (Kompass);Temp (C);Taupunkt (C);Feuchte (%);Luftdruck (hPa);Sonnenstrahlung (W/m2);UV-Index;Regenrate (mm/hr);Regen heute (mm)";
    const rows = [...data].reverse().map(d => [
      fmtTs(d.measured_at),
      lage,
      d.wind_speed      != null ? d.wind_speed.toFixed(2)        : "",
      d.wind_gust       != null ? d.wind_gust.toFixed(2)         : "",
      d.wind_direction  != null ? Math.round(d.wind_direction)   : "",
      d.wind_direction  != null ? degToCompass(d.wind_direction) : "",
      d.temperature     != null ? d.temperature.toFixed(1)       : "",
      d.dew_point       != null ? d.dew_point.toFixed(1)         : "",
      d.humidity        != null ? Math.round(d.humidity)         : "",
      d.pressure        != null ? d.pressure.toFixed(1)          : "",
      d.solar_radiation != null ? d.solar_radiation.toFixed(1)   : "",
      d.uv_index        != null ? d.uv_index.toFixed(1)          : "",
      d.rain_rate       != null ? d.rain_rate.toFixed(2)         : "",
      d.rain_daily      != null ? d.rain_daily.toFixed(2)        : "",
    ].join(";"));
    const blob = new Blob([BOM + header + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sensor.sensor_name}_alles_${hours}h.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sorted = [...data].reverse();

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ color: "#94a3b8", fontSize: 13 }}>{sorted.length} Einträge</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportCsvWind} style={styles.exportBtn}>
            ⬇ CSV Export Wind
          </button>
          <button onClick={exportCsvFull} style={styles.exportBtnFull}>
            ⬇ CSV Export Alles
          </button>
        </div>
      </div>
      <div style={{ overflowY: "auto", maxHeight: 340, borderRadius: 10, border: "1px solid #334155" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#1e293b", position: "sticky", top: 0 }}>
              {["Zeitstempel","Lage","Wind (m/s)","Böe (m/s)","Richtung","Temp (C)","Feuchte (%)"].map(h => (
                <th key={h} style={{ padding: "8px 10px", color: "#64748b", fontWeight: 600, textAlign: "left", borderBottom: "1px solid #334155", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((d, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                <td style={{ padding: "6px 10px", color: "#cbd5e1", whiteSpace: "nowrap" }}>
                  {fmtTs(d.measured_at)}
                </td>
                <td style={{ padding: "6px 10px", color: "#94a3b8" }}>{lage}</td>
                <td style={{ padding: "6px 10px", color: "#3b82f6", fontWeight: 500 }}>{d.wind_speed != null ? d.wind_speed.toFixed(1) : "-"}</td>
                <td style={{ padding: "6px 10px", color: "#f97316" }}>{d.wind_gust != null ? d.wind_gust.toFixed(1) : "-"}</td>
                <td style={{ padding: "6px 10px", color: "#94a3b8", whiteSpace: "nowrap" }}>{degToCompass(d.wind_direction)}</td>
                <td style={{ padding: "6px 10px", color: "#94a3b8" }}>{d.temperature != null ? d.temperature.toFixed(1) : "-"}</td>
                <td style={{ padding: "6px 10px", color: "#94a3b8" }}>{d.humidity != null ? Math.round(d.humidity) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonitoringTab({ logs, loginLogs, sensors, isAdmin, onRefresh }) {
  const [editingMac, setEditingMac] = useState(null);
  const [macValue, setMacValue] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  const saveMac = async (sensorId) => {
    try {
      await api.put(`/sensors/${sensorId}`, { gateway_mac: macValue });
      setEditingMac(null);
      onRefresh();
    } catch (e) { alert("Fehler beim Speichern"); }
  };

  const toggleActive = async (sensor) => {
    setTogglingId(sensor.id);
    try {
      await api.put(`/sensors/${sensor.id}`, { is_active: !sensor.is_active });
      onRefresh();
    } catch (e) { alert("Fehler beim Ändern des Status"); }
    finally { setTogglingId(null); }
  };

  const eventLabel = (event) => {
    if (event === "login")        return { text: "✓ Login",          bg: "#14532d", color: "#22c55e" };
    if (event === "logout")       return { text: "→ Logout",         bg: "#1e3a5f", color: "#60a5fa" };
    if (event === "login_failed") return { text: "✗ Fehlgeschlagen", bg: "#450a0a", color: "#ef4444" };
    return { text: event, bg: "#1e293b", color: "#94a3b8" };
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={styles.sectionTitle}>System-Monitoring</h2>
        <button style={styles.refBtn} onClick={onRefresh}>↻ Aktualisieren</button>
      </div>

      {isAdmin && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Benutzer-Aktivität (Login-Protokoll)</h3>
          <div style={{ overflowY: "auto", maxHeight: 480, borderRadius: 8, border: "1px solid #334155" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#0f172a", position: "sticky", top: 0 }}>
                  {["Zeitstempel", "Benutzer", "Ereignis", "IP-Adresse", "Dauer", "Browser"].map(h => (
                    <th key={h} style={{ padding: "8px 12px", color: "#64748b", fontWeight: 600, textAlign: "left", borderBottom: "1px solid #334155", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loginLogs.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: "20px", color: "#334155", textAlign: "center" }}>Noch keine Einträge</td></tr>
                ) : loginLogs.map((l, i) => {
                  const ev = eventLabel(l.event);
                  return (
                    <tr key={l.id} style={{ background: i % 2 === 0 ? "#0f172a" : "#1e293b" }}>
                      <td style={{ padding: "7px 12px", color: "#cbd5e1", whiteSpace: "nowrap" }}>
                        {new Date(l.logged_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </td>
                      <td style={{ padding: "7px 12px", color: "#e2e8f0", fontWeight: 600 }}>{l.username}</td>
                      <td style={{ padding: "7px 12px" }}>
                        <span style={{ background: ev.bg, color: ev.color, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{ev.text}</span>
                      </td>
                      <td style={{ padding: "7px 12px", color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>{l.ip_address ?? "–"}</td>
                      <td style={{ padding: "7px 12px", color: "#94a3b8" }}>{l.duration ?? "–"}</td>
                      <td style={{ padding: "7px 12px", color: "#475569", fontSize: 11, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.user_agent ?? "–"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Sensor-Pairing & MAC-Verifikation</h3>
        <table style={styles.table}>
          <thead>
            <tr>
              {["Name", "Gateway-MAC", "Gateway", "Sensor", "Etage", "Aktiv", "Status", "Letzter Empfang"].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sensors.map(s => (
              <tr key={s.id}>
                <td style={styles.td}><strong>{s.name}</strong></td>
                <td style={styles.td}>
                  {isAdmin && editingMac === s.id ? (
                    <div style={{ display: "flex", gap: 6 }}>
                      <input
                        value={macValue}
                        onChange={e => setMacValue(e.target.value)}
                        style={{ ...styles.macInput, width: 160 }}
                        placeholder="XX:XX:XX:XX:XX:XX"
                      />
                      <button style={styles.saveBtn} onClick={() => saveMac(s.id)}>✓</button>
                      <button style={styles.cancelBtn} onClick={() => setEditingMac(null)}>✕</button>
                    </div>
                  ) : (
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                      {s.gateway_mac}
                      {isAdmin && (
                        <button style={styles.editBtn} onClick={() => { setEditingMac(s.id); setMacValue(s.gateway_mac); }}>
                          ✎
                        </button>
                      )}
                    </span>
                  )}
                </td>
                <td style={styles.td}>{s.gateway_name}</td>
                <td style={styles.td}>{s.sensor_name ?? "–"}{s.sensor_mac && <span style={{ color: "#e2e8f0", fontSize: 12, fontFamily: "monospace", marginLeft: 8 }}>ID: {s.sensor_mac}</span>}</td>
                <td style={styles.td}>{s.floor ? `${s.floor}. OG` : "–"}</td>
                <td style={styles.td}>
                  {isAdmin ? (
                    <button
                      onClick={() => toggleActive(s)}
                      disabled={togglingId === s.id}
                      style={{
                        background: s.is_active ? "#14532d" : "#450a0a",
                        color: s.is_active ? "#22c55e" : "#ef4444",
                        border: "none", borderRadius: 5,
                        padding: "3px 12px", fontSize: 12,
                        cursor: togglingId === s.id ? "wait" : "pointer",
                        fontWeight: 600, minWidth: 70,
                      }}
                    >
                      {togglingId === s.id ? "…" : s.is_active ? "✓ Aktiv" : "✗ Inaktiv"}
                    </button>
                  ) : (
                    <span style={{ color: s.is_active ? "#22c55e" : "#ef4444", fontSize: 12, fontWeight: 600 }}>
                      {s.is_active ? "✓ Aktiv" : "✗ Inaktiv"}
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.statusBadge,
                    background: s.status === "ok" ? "#14532d" : s.status === "warning" ? "#422006" : "#450a0a",
                    color: s.status === "ok" ? "#22c55e" : s.status === "warning" ? "#eab308" : "#ef4444",
                  }}>
                    {s.status === "ok" ? "✓ Online" : s.status === "warning" ? "⚠ Verzögert" : s.status === "offline" ? "✗ Offline" : "? –"}
                  </span>
                </td>
                <td style={{ ...styles.td, color: "#64748b", fontSize: 12 }}>
                  {s.last_seen ? new Date(s.last_seen).toLocaleString("de-CH") : "–"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isAdmin && <div style={styles.roNote}>📋 MAC-Adressen können nur vom Administrator geändert werden.</div>}
      </div>

      <div style={styles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={styles.cardTitle}>Datenabruf-Protokoll</h3>
          <span style={{ color: "#475569", fontSize: 12 }}>{logs.length} Einträge · Abruf alle 5 Min.</span>
        </div>
        <FetchLogViewer logs={logs} />
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#0f172a", display: "flex", flexDirection: "column" },
  nav: { background: "#0d1526", borderBottom: "1px solid #1e3a5f", padding: "0 24px", height: 112, display: "flex", alignItems: "center", gap: 20, flexShrink: 0 },
  navLeft: { display: "flex", alignItems: "center", gap: 8 },
  navLogo: { fontSize: 38 },
  navTitle: { color: "#f1f5f9", fontWeight: 800, fontSize: 17 },
  navSub: { color: "#94a3b8", fontSize: 13 },
  tabs: { display: "flex", gap: 4, flex: 1, justifyContent: "center" },
  tab: { background: "#1e3a5f", border: "1px solid #2a4a6a", color: "#94a3b8", padding: "7px 18px", borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 600, transition: "all 0.15s" },
  tabActive: { background: "#1d4ed8", color: "#ffffff", borderColor: "#3b82f6" },
  navRight: { display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" },
  adminBadge: { background: "#1d4ed8", color: "#fff", fontSize: 11, borderRadius: 4, padding: "2px 8px", fontWeight: 700 },
  userLabel: { color: "#e2e8f0", fontSize: 13, fontWeight: 600 },
  logoutBtn: { background: "#1e3a5f", border: "1px solid #3b82f6", color: "#e2e8f0", borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  main: { flex: 1, padding: "24px", overflow: "auto" },
  loading: { color: "#64748b", textAlign: "center", marginTop: 60, fontSize: 16 },
  sectionTitle: { color: "#f1f5f9", fontSize: 18, fontWeight: 700, margin: 0 },
  gridHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  gridHint: { color: "#cbd5e1", fontSize: 12 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 },
  sensorList: { width: 160, flexShrink: 0, display: "flex", flexDirection: "column", gap: 6 },
  sensorListTitle: { color: "#64748b", fontSize: 12, marginBottom: 4, fontWeight: 600 },
  sensorItem: { borderRadius: 8, padding: "10px 12px", cursor: "pointer", color: "#e2e8f0", fontSize: 13, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" },
  floorBadge: { color: "#64748b", fontSize: 11 },
  noSelection: { color: "#475569", textAlign: "center", marginTop: 80, fontSize: 16 },
  noData: { color: "#64748b", textAlign: "center", marginTop: 40 },
  hourBtns: { display: "flex", gap: 4 },
  hourBtn: { color: "#e2e8f0", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" },
  exportBtn:     { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 7, padding: "6px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  exportBtnFull: { background: "#0f766e", color: "#fff", border: "none", borderRadius: 7, padding: "6px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  card: { background: "#1e293b", borderRadius: 12, padding: 20 },
  cardTitle: { color: "#f1f5f9", fontSize: 15, fontWeight: 700, margin: "0 0 16px" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { color: "#64748b", fontSize: 12, fontWeight: 600, textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #334155" },
  td: { color: "#e2e8f0", fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #1e3a5f" },
  statusBadge: { borderRadius: 4, padding: "2px 8px", fontSize: 12, fontWeight: 600 },
  refBtn: { background: "#334155", color: "#e2e8f0", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  editBtn: { background: "transparent", border: "none", color: "#3b82f6", cursor: "pointer", marginLeft: 6, fontSize: 13 },
  macInput: { background: "#0f172a", border: "1px solid #3b82f6", borderRadius: 4, color: "#f1f5f9", padding: "3px 8px", fontSize: 12 },
  saveBtn: { background: "#14532d", color: "#22c55e", border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer" },
  cancelBtn: { background: "#450a0a", color: "#ef4444", border: "none", borderRadius: 4, padding: "3px 8px", cursor: "pointer" },
  roNote: { color: "#475569", fontSize: 12, marginTop: 12, fontStyle: "italic" },
};
